import { ACTION_STATUSES, TRACK_EVENT_TYPES, TRACK_EVENT_NAMES, PAYMENT_TYPE } from 'shared/consts';
import { useBooleanState } from '@hqo/react-components-library/dist/hooks';
import { BUTTON_VARIANT } from '@hqo/react-components-library/dist/utils';
import { CheckoutContainer, StyledBottomMobileModal } from './styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { deletePaymentMethod, getPaymentMethods, setCurrentPaymentMethodId } from 'store/payment/actions';
import {
  selectSubmitCartStatus,
  shouldSubmit3DSecure,
  selectCartDiscountSummary,
  selectCartId,
  select3DSecure,
} from 'store/cart/selectors';
import {
  selectCurrentPaymentMethodId,
  selectDeletePaymentMethodsStatus,
  selectSavePaymentMethodStatus,
} from 'store/payment/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { CheckoutDesktop } from './components/checkout-desktop';
import { CheckoutDesktopModalWrapper } from 'components/modals/checkoutModalWrapper';
import { ContentModal } from './components/content-modal';
import { useCart } from 'hooks/use-cart.hook';
import { useIntl } from 'react-intl';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { useLocation } from 'react-router-dom';
import { usePaymentMethods } from 'hooks/use-payment-methods.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { track } from '@hqo/web-tracking';
import { formatSummaryValues } from 'utils/formatSummaryValues';
import { isNo3DSPaymentMethod } from 'utils/isNo3DSPaymentMethod';
import { formatCartItemsIds } from 'utils/formatTrackEventProps';
import { CheckoutMobileContent } from './components/checkout-mobile-content';
import { getTransaction } from 'store/transactions/actions';
import { selectTransactionStatus } from 'store/transactions/selectors';
import { selectCurrentLocationId } from 'store/app-instance-configs/selectors';
import { resetComplete3DSCart, resetGetCartStatus, resetSubmitCartStatus, submitCart } from 'store/cart/actions';
import qs from 'qs';
import { searchParams } from 'pages/resource/date-select-modal/interface';
import { ThreeDSModal } from 'components/modals/3dsModal';
import { INVOICE } from 'shared/consts/payment-method-types';
import { Spinner } from '@hqo/react-components-library/dist/atoms/spinner';
import { defaultTheme } from '@hqo/react-components-library/dist/molecules/theme';
import { Container } from 'hocs/shared-styles';
import { useAddCardButton } from 'hooks/use-add-card-button.hook';
import { resetAddOnsState } from 'store/add-ons/actions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNativePaymentMethod } from 'components/payment/quick-checkout/components/use-native-payment-method.hook';
import { useMiniappSdk } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { resetSpreedlyStatus } from 'store/add-card/actions';
import { SavedPaymentMethod } from 'store/payment/types';
import { selectResource } from 'store/resource/selectors';
import { getApplicablePaymentMethods } from 'utils/getApplicablePaymentMethods';

interface CheckoutProps {
  onCloseCheckout: VoidFunction;
  openReceipt: VoidFunction;
}

export const Checkout = ({ onCloseCheckout, openReceipt }: CheckoutProps): JSX.Element => {
  const intl = useIntl();
  const client = useMiniappSdk();
  const location = useLocation();
  const { enableHqoMiniappApplePay } = useFlags();
  const [submittedPaymentMethodId, setSubmittedPaymentMethodId] = useState<number>(undefined);
  const paymentId = useSelector(selectCurrentPaymentMethodId);
  const { expandAddCard, error, cartId: currentCartId, ...queryParams } = useSearchParams<searchParams>();
  const cartId = useSelector(selectCartId) || currentCartId;
  const locationId = useSelector(selectCurrentLocationId);
  const [paymentError, setPaymentError] = useState<boolean>(false);

  const dispatch = useDispatch();
  const isMobileDevice = useIsSmallViewportWidth();
  const { isCartFulfilled, cart } = useCart(cartId);
  const { paymentMethods } = usePaymentMethods();
  const { setIsAddCardButtonDisabled, isAddCardButtonDisabled } = useAddCardButton(!!expandAddCard);
  const submit3DSecure = useSelector(shouldSubmit3DSecure);
  const { total, currencyType } = formatSummaryValues(cart?.total_summary);
  const resource = useSelector(selectResource);

  const savePaymentMethodStatus = useSelector(selectSavePaymentMethodStatus);
  const submitCartStatus = useSelector(selectSubmitCartStatus);
  const deletePaymentMethodsStatus = useSelector(selectDeletePaymentMethodsStatus);

  const discountSummary = useSelector(selectCartDiscountSummary);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string>(null);
  const { value: showConfirmCloseModal, toggle: toggleConfirmCloseModal } = useBooleanState(false);
  const { value: reverseAnimation, toggle: toggleReverseAnimation } = useBooleanState(false);
  const { value: showAddCardForm, toggle: toggleAddCardForm } = useBooleanState(!!expandAddCard);
  const [show3DS, setShow3DS] = useState<boolean>(false);
  const transactionStatus = useSelector(selectTransactionStatus);
  const { is3DSecureSupported } = useSelector(select3DSecure);
  const cartItemsIds = formatCartItemsIds(cart);
  const { isApplePayMethodSelected, setPendingNativePayment, pendingNativePayment, isNativePaymentError } =
    useNativePaymentMethod({
      cart,
      locationId,
    });

  const applicablePaymentMethods = useMemo(
    () => getApplicablePaymentMethods(paymentMethods, cart),
    [paymentMethods, cart],
  );

  const currentPaymentMethod = useMemo<SavedPaymentMethod>(() => {
    if (paymentMethods?.length === 1 && paymentMethods[0].payment_method_type === INVOICE) {
      return paymentMethods[0];
    }

    if (enableHqoMiniappApplePay && navigator.userAgent.includes('iPhone')) {
      // Constrain to applicable methods so a stale/hidden id (e.g. credits on a resource
      // without credit pricing) can't resolve to a method that isn't shown in the list.
      // Apple Pay itself uses the synthetic '-1' id, which isn't a real method here, so it
      // stays undefined and is tracked separately via isApplePayMethodSelected.
      return applicablePaymentMethods.find(paymentMethod => paymentMethod.id.toString() === paymentId);
    }

    // Prefer the method already selected in the store (set by quick checkout, add-card
    // success, etc.), falling back to the account default and then the first applicable
    // method. Only applicable methods are considered so a stale/hidden method (e.g. credits
    // on a resource without credit pricing) can never be shown as selected or submitted.
    const selectedByStore = applicablePaymentMethods.find(paymentMethod => paymentMethod.id.toString() === paymentId);

    return (
      selectedByStore ??
      applicablePaymentMethods.find(paymentMethod => paymentMethod.default) ??
      applicablePaymentMethods[0]
    );
  }, [enableHqoMiniappApplePay, paymentMethods, applicablePaymentMethods, paymentId]);

  useEffect(() => {
    const isExternalPaymentMethod = paymentId === PAYMENT_TYPE.GOOGLE_PAY || paymentId === PAYMENT_TYPE.EXTERNAL;
    const isApplePayFlow = enableHqoMiniappApplePay && navigator.userAgent.includes('iPhone');

    // Only correct an actively selected method that EXISTS in the account but is not
    // applicable to this cart (e.g. stale credits carried over from another resource).
    // We deliberately do NOT touch ids that are absent from the full list: a just-added
    // card is set as selected before the payment-methods refetch lands, and resetting it
    // here would race that refetch and drop the new card. The empty-selection case is
    // handled by usePaymentMethods, so we skip it too.
    if (!paymentId || isExternalPaymentMethod || isApplePayFlow || !applicablePaymentMethods.length) {
      return;
    }

    const existsInAccount = paymentMethods?.some(paymentMethod => paymentMethod.id.toString() === paymentId);
    const isCurrentApplicable = applicablePaymentMethods.some(
      paymentMethod => paymentMethod.id.toString() === paymentId,
    );

    if (existsInAccount && !isCurrentApplicable) {
      const fallback =
        applicablePaymentMethods.find(paymentMethod => paymentMethod.default) ?? applicablePaymentMethods[0];
      dispatch(setCurrentPaymentMethodId(fallback.id.toString()));
    }
  }, [applicablePaymentMethods, paymentMethods, paymentId, enableHqoMiniappApplePay, dispatch]);

  useEffect(() => {
    if (paymentId) {
      setSubmittedPaymentMethodId(Number(paymentId));
    }
  }, [paymentId]);

  useEffect(() => {
    setDefaultPaymentMethod(currentPaymentMethod?.id?.toString());
  }, [paymentMethods, currentPaymentMethod]);

  useEffect(() => {
    if (error === 'true') {
      setPaymentError(true);
    }
  }, [error]);

  const onStayClick = useCallback(() => {
    toggleReverseAnimation();
    setTimeout(() => {
      toggleConfirmCloseModal();
      toggleReverseAnimation();
    }, 300);
  }, [toggleConfirmCloseModal]);

  const onLeaveClick = useCallback(() => {
    toggleConfirmCloseModal();
    onCloseCheckout();
  }, [onCloseCheckout, toggleConfirmCloseModal]);

  const onBackClick = useCallback(() => {
    onCloseCheckout();
  }, [onCloseCheckout]);

  const onCloseClick = useCallback(() => {
    toggleConfirmCloseModal();
  }, [toggleConfirmCloseModal]);

  useEffect(() => {
    if (submitCartStatus === ACTION_STATUSES.FULFILLED) {
      dispatch(getTransaction.request(cart?.transaction_uuid));
    }
  }, [cart?.transaction_uuid, dispatch, submitCartStatus]);

  useEffect(() => {
    const hasSuccessfullyPurchasedWithCreditCard =
      submitCartStatus === ACTION_STATUSES.FULFILLED && transactionStatus === ACTION_STATUSES.FULFILLED;

    if (hasSuccessfullyPurchasedWithCreditCard && (!submit3DSecure || isNo3DSPaymentMethod(defaultPaymentMethod))) {
      openReceipt();
      dispatch(resetAddOnsState());
      dispatch(resetSubmitCartStatus());
      dispatch(resetGetCartStatus());
    }
  }, [submitCartStatus, dispatch, openReceipt, transactionStatus, defaultPaymentMethod, submit3DSecure]);

  useEffect(() => {
    if (showAddCardForm && savePaymentMethodStatus === ACTION_STATUSES.FULFILLED) {
      toggleAddCardForm();
    }
  }, [savePaymentMethodStatus]);

  const onRemovePaymentMethod = useCallback(() => {
    dispatch(
      deletePaymentMethod.request({
        paymentMethodId: currentPaymentMethod?.id,
        paymentMethods,
      }),
    );
  }, [dispatch, currentPaymentMethod, paymentMethods]);

  useEffect(() => {
    if (deletePaymentMethodsStatus === ACTION_STATUSES.FULFILLED) {
      dispatch(getPaymentMethods.request(cartId));
    }
  }, [dispatch, cartId, deletePaymentMethodsStatus]);

  const onAddPaymentMethodClick = useCallback(() => {
    dispatch(resetSpreedlyStatus());
    toggleAddCardForm();
    setIsAddCardButtonDisabled(true);
  }, [toggleAddCardForm, setIsAddCardButtonDisabled]);

  const onCancelAddCardClick = useCallback(() => {
    setIsAddCardButtonDisabled(false);
    dispatch(resetSpreedlyStatus());
    toggleAddCardForm();
  }, [toggleAddCardForm]);

  const buttons = [
    {
      title: intl.formatMessage({ id: 'stay' }),
      handler: onStayClick,
    },
    {
      title: intl.formatMessage({ id: 'leave' }),
      handler: onLeaveClick,
      variant: BUTTON_VARIANT.SECONDARY,
    },
  ];

  useEffect(() => {
    if (isCartFulfilled) {
      track(
        TRACK_EVENT_NAMES.FULL_CHECKOUT_IMPRESSION,
        {
          type: TRACK_EVENT_TYPES.IMPRESSION,
          cart_total: total,
          currency_code: currencyType,
          promo_code_applied: !!discountSummary,
          cart_type: cart.type,
          items: cartItemsIds,
          adapter: cart.config?.config?.adapter,
        },
        { sendToHqoTracking: true },
      );
    }
  }, [isCartFulfilled]);

  const onPayClick = useCallback(() => {
    track(
      TRACK_EVENT_NAMES.CHECKOUT_CLICK,
      {
        type: TRACK_EVENT_TYPES.ACTION,
        payment_method: currentPaymentMethod?.payment_method_type,
        cart_total: total,
        currency_code: currencyType,
        promo_code_applied: !!discountSummary,
        cart_type: cart.type,
        items: cartItemsIds,
        adapter: cart.config?.config?.adapter,
      },
      { sendToHqoTracking: true },
    );
    setPaymentError(false);
    setSubmittedPaymentMethodId(currentPaymentMethod?.id);
    dispatch(resetComplete3DSCart());
    if (isApplePayMethodSelected) {
      setPendingNativePayment(true);
      client?.payments.showNativePayment(
        null,
        {
          currency: cart.total_summary.currency_type,
          total: cart.total_summary.total / 100,
          tax: cart.total_summary.tax_summary.taxes_total / 100,
          authorization_total: cart.total_summary.total / 100,
        },
        cart.config.payment_gateway_id,
      );
    } else {
      const paymentInfo = {
        cart_id: cartId,
        payment_method_id: defaultPaymentMethod,
        locationId,
      };

      const queryString = qs.stringify({ ...queryParams, cartId, paymentId: paymentInfo.payment_method_id });

      dispatch(
        submitCart.request(
          submit3DSecure && !isNo3DSPaymentMethod(defaultPaymentMethod)
            ? {
                ...paymentInfo,
                browser_info: window.Spreedly?.ThreeDS?.serialize('01'),
                attempt_3dsecure: true,
                three_ds_version: '2',
                callback_url: `${window.location.origin}/redirect?${queryString}`,
                redirect_url: `${window.location.origin}/redirect?${queryString}`,
              }
            : paymentInfo,
        ),
      );
    }
  }, [
    currentPaymentMethod,
    defaultPaymentMethod,
    total,
    currencyType,
    discountSummary,
    cart,
    dispatch,
    cartId,
    cartItemsIds,
    locationId,
    location.search,
    submit3DSecure,
    isApplePayMethodSelected,
  ]);

  return (
    <CheckoutContainer data-testid="checkout-modal">
      {!show3DS ? (
        <>
          {isMobileDevice
            ? isCartFulfilled && (
                <CheckoutMobileContent
                  cart={cart}
                  onRemovePaymentMethod={onRemovePaymentMethod}
                  defaultPaymentMethod={defaultPaymentMethod}
                  submittedPaymentMethodId={submittedPaymentMethodId}
                  paymentError={paymentError}
                  paymentMethods={paymentMethods}
                  onAddPaymentMethodClick={onAddPaymentMethodClick}
                  onPayClick={onPayClick}
                  submitCartStatus={submitCartStatus}
                  isCartFulfilled={isCartFulfilled}
                  showAddCardForm={showAddCardForm}
                  cartId={cartId}
                  onCancelAddCardClick={onCancelAddCardClick}
                  isAddCardButtonDisabled={isAddCardButtonDisabled}
                  setIsAddCardButtonDisabled={setIsAddCardButtonDisabled}
                  pendingNativePayment={pendingNativePayment}
                  isNativePaymentError={isNativePaymentError}
                  autoConfirm={resource.auto_confirm}
                />
              )
            : isCartFulfilled && (
                <CheckoutDesktopModalWrapper
                  visible
                  withBackButton
                  onPressBack={onBackClick}
                  content={
                    <CheckoutDesktop
                      cart={cart}
                      onRemovePaymentMethod={onRemovePaymentMethod}
                      currentPaymentMethodId={defaultPaymentMethod}
                      submittedPaymentMethodId={submittedPaymentMethodId}
                      currentPaymentError={paymentError}
                      paymentMethods={paymentMethods}
                      onAddPaymentMethodClick={onAddPaymentMethodClick}
                      onPayClick={onPayClick}
                      submitCartStatus={submitCartStatus}
                      showAddCardForm={showAddCardForm}
                      cartId={cartId}
                      onCancelAddCardClick={onCancelAddCardClick}
                      isAddCardButtonDisabled={isAddCardButtonDisabled}
                      setIsAddCardButtonDisabled={setIsAddCardButtonDisabled}
                      isNativePaymentError={isNativePaymentError}
                      autoConfirm={resource.auto_confirm}
                    />
                  }
                  onClose={onCloseClick}
                />
              )}
        </>
      ) : (
        <Container data-testid="loading-container" isLoading isModal>
          <Spinner size="10em" color={defaultTheme.colors.$greyLight} />
        </Container>
      )}
      {is3DSecureSupported && (
        <ThreeDSModal
          setPaymentError={setPaymentError}
          setIs3DSVisible={setShow3DS}
          transactionToken={cart?.transaction?.token}
          spreedlyToken={cart?.ui_enablements.spreedly_env_token}
        />
      )}
      <StyledBottomMobileModal
        reverseAnimation={reverseAnimation}
        isVisible={showConfirmCloseModal}
        buttons={buttons}
        content={ContentModal()}
        showCloseIcon={false}
      />
    </CheckoutContainer>
  );
};
